import { createReferenceMatcher, referenceTargets } from './connectedBible'
import type { ReferenceTarget, StoryProject } from './types'

export type BibleSearchResult = ReferenceTarget & {
  matchLabel?: string
  excerpt?: string
}

function excerpt(text: string, query = '') {
  const compact = text.replace(/\s+/g, ' ').trim()
  const hit = query ? compact.toLowerCase().indexOf(query) : 0
  const start = Math.max(0, hit - 35)
  const end = Math.min(compact.length, start + 135)
  return (
    (start ? '…' : '') +
    compact.slice(start, end) +
    (end < compact.length ? '…' : '')
  )
}

export function searchBible(
  project: StoryProject,
  value: string,
): BibleSearchResult[] {
  const query = value.trim().replace(/\s+/g, ' ').toLowerCase()
  const characters = new Map(
    project.bible?.characters.map((c) => ['character:' + c.id, c]),
  )
  const entries = new Map(
    project.bible?.entries?.map((e) => ['entry:' + e.id, e]),
  )
  const results: BibleSearchResult[] = referenceTargets(project)
    .filter((target) => target.kind !== 'scene')
    .flatMap((target) => {
      if (!query) return [target]
      const character = characters.get(target.id)
      const entry = entries.get(target.id)
      const item = character || entry
      const fields: [string, string][] = [
        ['Name', target.title],
        ['Section', target.section],
        ...(character
          ? ([
              ['Role', character.role],
              ['Motivation & background', character.description],
              ['Voice & mannerisms', character.voice],
            ] as [string, string][])
          : []),
        ...(entry
          ? ([
              ['At a glance', entry.summary],
              ['Description & notes', entry.body],
            ] as [string, string][])
          : []),
        ...(item?.images || []).map((image): [string, string] => [
          'Picture',
          [image.name, image.alt].join(' · '),
        ]),
        ...(item?.references || []).map((ref): [string, string] => [
          'Reference link',
          [ref.label, ref.url].join(' · '),
        ]),
      ]
      const matched = fields.find(([, text]) =>
        text.replace(/\s+/g, ' ').toLowerCase().includes(query),
      )
      if (!matched) return []
      return [
        {
          ...target,
          matchLabel: matched[0],
          excerpt: excerpt(matched[1], query),
        },
      ]
    })
  const relevance = (result: BibleSearchResult) =>
    result.title.toLowerCase() === query
      ? 0
      : result.matchLabel === 'Name'
        ? 1
        : 2
  return query ? results.sort((a, b) => relevance(a) - relevance(b)) : results
}

export type ReferenceScene = {
  nodeId: string
  title: string
  sceneNumber: number
  chapter: string
  dialogueLines: number
  mentions: number
  planningMentions: number
  excerpt: string
}

export function referenceScenes(
  project: StoryProject,
  targetId: string,
): ReferenceScene[] {
  if (!referenceTargets(project).some((target) => target.id === targetId))
    return []
  const character = project.bible?.characters.find(
    (c) => 'character:' + c.id === targetId,
  )
  const name = character?.name.trim().toLowerCase()
  const match = createReferenceMatcher(project)
  return project.nodes.flatMap((node, index) => {
    let dialogueLines = 0,
      mentions = 0,
      planningMentions = 0,
      preview = ''
    const count = (text: string, planning = false) => {
      const found = match('scene:' + node.id, text).filter((m) =>
        m.targetIds.includes(targetId),
      )
      if (planning) planningMentions += found.length
      else mentions += found.length
      if (found.length && !preview)
        preview = excerpt(text, found[0].title.toLowerCase())
    }
    for (const block of node.blocks) {
      if (
        name &&
        block.kind === 'dialogue' &&
        block.speaker?.trim().toLowerCase() === name
      ) {
        dialogueLines++
        if (!preview) preview = excerpt(block.text)
      }
      count(block.text)
      count(block.direction || '')
    }
    for (const choice of node.choices) count(choice.label)
    count(node.synopsis || '', true)
    count(node.notes || '', true)
    if (!dialogueLines && !mentions && !planningMentions) return []
    return [
      {
        nodeId: node.id,
        title: node.title,
        sceneNumber: index + 1,
        chapter: node.chapter || '',
        dialogueLines,
        mentions,
        planningMentions,
        excerpt: preview,
      },
    ]
  })
}
